import {
  BadgePercent,
  Briefcase,
  Building2,
  FolderTree,
  GraduationCap,
  Handshake,
  HelpCircle,
  ListChecks,
  Home,
  MapPin,
  MessageSquareText,
  MousePointerClick,
  Newspaper,
  PenLine,
  Radar,
  Settings,
  Signal,
  Signpost,
  Star,
  Tag,
  ToggleRight,
  UserRound,
  Users,
  Wrench,
} from 'lucide-react';

import { Role } from '@/entities/user/model/user.types';

export interface NavItem {
  title: string;
  href: string;
  icon: React.ElementType;
  /** Absent means every signed-in staff role. */
  roles?: Role[];
}

export interface NavGroup {
  /** The small caps heading above the group. `null` renders the items bare. */
  label: string | null;
  items: NavItem[];
}

/**
 * The admin's navigation, once.
 *
 * This lived twice: ten `NavItem[]` arrays in `sidebar.tsx` and a separate flat
 * array in `mobile-sidebar.tsx`, with nothing tying them together. They had
 * drifted badly — the desktop rail carried 31 destinations and the mobile drawer
 * 20, and the eleven missing were the entire catalog: tools, brands, agencies,
 * their categories and tags, and experts. On a phone there was no way to reach
 * any of it.
 *
 * The `roles` did *not* diverge, which is the one piece of luck here: every
 * entry present in both listed the same roles, so unifying is a union of
 * destinations rather than a permission decision.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    label: null,
    items: [
      {
        title: 'Dashboard',
        href: '/dashboard',
        icon: Home,
      },
    ],
  },
  {
    label: 'Tools',
    items: [
      {
        title: 'Tools',
        href: '/dashboard/tools',
        icon: Wrench,
        roles: [Role.MANAGER, Role.ADMIN, Role.SUPER_ADMIN],
      },
      /*
       * Second in its section, directly under the list it promotes from. There is
       * exactly one of these site-wide, so it is a single screen rather than a
       * table — but it belongs beside tools, not in a "Featured" section of its
       * own, which separated it from everything it works on.
       */
      {
        title: 'Tool of the Month',
        href: '/dashboard/featured/tool-of-the-month',
        icon: Star,
        roles: [Role.ADMIN, Role.SUPER_ADMIN],
      },
      {
        title: 'Tool Categories',
        href: '/dashboard/tools/categories',
        icon: FolderTree,
        roles: [Role.MANAGER, Role.ADMIN, Role.SUPER_ADMIN],
      },
      {
        title: 'Tool Tags',
        href: '/dashboard/tools/tags',
        icon: Tag,
        roles: [Role.MANAGER, Role.ADMIN, Role.SUPER_ADMIN],
      },
      {
        title: 'Tool FAQs',
        href: '/dashboard/tools/faqs',
        icon: HelpCircle,
        roles: [Role.MANAGER, Role.ADMIN, Role.SUPER_ADMIN],
      },
      /*
       * No Tool Key Features, Cases or Testimonials here, and no "Tools
       * Content" group holding the last two.
       *
       * All three were catalog-wide tables of rows that each belong to exactly
       * one tool, and the first field of every one of their forms was a tool
       * picker. They are sections of the tool editor now — see
       * `ToolSectionsCard`. Tool FAQs stays a screen of its own because a FAQ
       * is shared: `faqIds` on the tool form links many tools to one row.
       */
    ],
  },
  {
    label: 'Brands',
    items: [
      {
        title: 'Brands',
        href: '/dashboard/brands',
        icon: Building2,
        roles: [Role.MANAGER, Role.ADMIN, Role.SUPER_ADMIN],
      },
      /*
       * Second in its section, directly under the list it promotes from. There is
       * exactly one of these site-wide, so it is a single screen rather than a
       * table — but it belongs beside brands, not in a "Featured" section of its
       * own, which separated it from everything it works on.
       */
      {
        title: 'Brand of the Month',
        href: '/dashboard/featured/brand-of-the-month',
        icon: Star,
        roles: [Role.ADMIN, Role.SUPER_ADMIN],
      },
      {
        title: 'Brand Categories',
        href: '/dashboard/brands/categories',
        icon: FolderTree,
        roles: [Role.MANAGER, Role.ADMIN, Role.SUPER_ADMIN],
      },
      {
        title: 'Brand Tags',
        href: '/dashboard/brands/tags',
        icon: Tag,
        roles: [Role.MANAGER, Role.ADMIN, Role.SUPER_ADMIN],
      },
      {
        title: 'Brand Locations',
        href: '/dashboard/brands/locations',
        icon: MapPin,
        roles: [Role.MANAGER, Role.ADMIN, Role.SUPER_ADMIN],
      },
      {
        title: 'Brand Data Review',
        href: '/dashboard/brands/review',
        icon: ListChecks,
        roles: [Role.MANAGER, Role.ADMIN, Role.SUPER_ADMIN],
      },
    ],
  },
  {
    label: 'Agencies',
    items: [
      {
        title: 'Agencies',
        href: '/dashboard/agencies',
        icon: Briefcase,
        roles: [Role.MANAGER, Role.ADMIN, Role.SUPER_ADMIN],
      },
      /*
       * Second in its section, directly under the list it promotes from. There is
       * exactly one of these site-wide, so it is a single screen rather than a
       * table — but it belongs beside agencys, not in a "Featured" section of its
       * own, which separated it from everything it works on.
       */
      {
        title: 'Agency of the Month',
        href: '/dashboard/featured/agency-of-the-month',
        icon: Star,
        roles: [Role.ADMIN, Role.SUPER_ADMIN],
      },
      {
        title: 'Agency Categories',
        href: '/dashboard/agencies/categories',
        icon: FolderTree,
        roles: [Role.MANAGER, Role.ADMIN, Role.SUPER_ADMIN],
      },
      /*
       * No Certifications, Agency Services, What Sets Us Apart or Work Samples
       * here — they are sections of the agency editor now, beside Case studies
       * and Testimonials. Same reason as the tools group above: each was a
       * catalog-wide table of rows belonging to exactly one agency, opened by
       * picking that agency in the form's first field.
       *
       * Agency Categories stays: a category is shared across agencies, so it is
       * genuinely its own record rather than one agency's content.
       */
    ],
  },
  {
    label: 'Resources',
    items: [
      {
        title: 'Articles',
        href: '/dashboard/resources',
        icon: Newspaper,
        roles: [Role.MANAGER, Role.ADMIN, Role.SUPER_ADMIN],
      },
      {
        title: 'Article Categories',
        href: '/dashboard/resources/categories',
        icon: FolderTree,
        roles: [Role.MANAGER, Role.ADMIN, Role.SUPER_ADMIN],
      },
      {
        title: 'Article Authors',
        href: '/dashboard/resources/authors',
        icon: PenLine,
        roles: [Role.MANAGER, Role.ADMIN, Role.SUPER_ADMIN],
      },
    ],
  },
  {
    label: 'Experts',
    items: [
      {
        title: 'Experts',
        href: '/dashboard/experts',
        icon: GraduationCap,
        roles: [Role.MANAGER, Role.ADMIN, Role.SUPER_ADMIN],
      },
    ],
  },
  {
    label: 'Users',
    items: [
      {
        title: 'Organizations',
        href: '/dashboard/organizations',
        icon: Building2,
        roles: [Role.SUPER_ADMIN, Role.ADMIN],
      },
      {
        title: 'Customers',
        href: '/dashboard/customers',
        icon: UserRound,
        roles: [Role.SUPER_ADMIN, Role.ADMIN],
      },
      {
        title: 'Founding rates',
        href: '/dashboard/founding-rates',
        icon: BadgePercent,
        roles: [Role.ADMIN, Role.SUPER_ADMIN],
      },
      {
        title: 'Internal Users',
        href: '/dashboard/users',
        icon: Users,
        // MANAGER is deliberately absent: `AdminUsersResolver` is
        // @Roles('ADMIN','SUPER_ADMIN'), so the link only ever led them to an error.
        roles: [Role.SUPER_ADMIN, Role.ADMIN],
      },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        title: 'Connection Requests',
        href: '/dashboard/connection-requests',
        icon: Handshake,
        roles: [Role.ADMIN, Role.SUPER_ADMIN],
      },
      {
        title: 'Click-Throughs',
        href: '/dashboard/tool-clicks',
        icon: MousePointerClick,
        roles: [Role.ADMIN, Role.SUPER_ADMIN],
      },
      {
        title: 'All Redirects',
        href: '/dashboard/redirects',
        icon: Signpost,
        roles: [Role.MANAGER, Role.ADMIN, Role.SUPER_ADMIN],
      },
    ],
  },
  {
    label: 'Workflows',
    items: [
      /*
       * These queues write logos straight onto tools, brands and agencies with
       * none of the ownership or publication checks the editors carry — that is
       * what makes a bulk triage screen fast — so the guard is narrower than the
       * MANAGER+ the editors themselves take. `AdminWorkflowsResolver` is
       * @Roles('ADMIN', 'SUPER_ADMIN'), and hiding the link is not what enforces
       * it: each page carries its own RoleGate.
       */
      {
        title: 'Workflows',
        href: '/dashboard/workflows',
        icon: ListChecks,
        roles: [Role.ADMIN, Role.SUPER_ADMIN],
      },
    ],
  },
  {
    label: 'System',
    items: [
      /*
       * The brand-stack detector, SUPER_ADMIN only.
       *
       * Narrower than the MANAGER+ the catalog editors take, for two reasons.
       * Queueing a crawl SPENDS MONEY — every brand is one billed remote-browser
       * session — and the fingerprints it edits are a detection asset with
       * no ownership or publication checks behind them. `AdminCrawlTargetsResolver`
       * and its three siblings are all @Roles('SUPER_ADMIN'); hiding this link is
       * not the enforcement, the section layout's RoleGate and the API guards are.
       *
       * One entry, not five. `activeNavItem` is longest-prefix so every route
       * under /dashboard/scraper highlights it, and the section carries its own
       * tab bar.
       */
      {
        title: 'Scraper',
        href: '/dashboard/scraper',
        icon: Radar,
        roles: [Role.SUPER_ADMIN],
      },
      {
        title: 'Signals',
        href: '/dashboard/signals',
        icon: Signal,
        roles: [Role.SUPER_ADMIN],
      },
      {
        title: 'Feature flags',
        href: '/dashboard/feature-flags',
        icon: ToggleRight,
        roles: [Role.SUPER_ADMIN],
      },
      {
        title: 'Quick answers',
        href: '/dashboard/quick-answers',
        icon: MessageSquareText,
        roles: [Role.SUPER_ADMIN],
      },
    ],
  },
  {
    label: null,
    items: [
      {
        title: 'Settings',
        href: '/dashboard/settings',
        icon: Settings,
      },
    ],
  },
];

/** Drop the entries this role may not see. No `roles` means every staff role. */
export function visibleGroups(user: { role: Role } | null): NavGroup[] {
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.roles || (user && item.roles.includes(user.role))),
  })).filter((group) => group.items.length > 0);
}

/**
 * The entry a path is "in".
 *
 * Longest prefix wins, so `/dashboard/tools/tags` highlights Tags rather than
 * Tools — every catalog section is nested under a list page that would otherwise
 * match everything below it.
 */
export function activeNavItem(pathname: string, groups: NavGroup[]): NavItem | null {
  const matches = groups
    .flatMap((group) => group.items)
    .filter((item) => pathname === item.href || pathname.startsWith(item.href + '/'));

  if (matches.length === 0) return null;
  return matches.reduce((best, item) => (item.href.length > best.href.length ? item : best));
}
